import {format} from 'date-fns'
import {pickBy} from 'lodash'
import * as z from 'zod/mini'
import type {Colors} from '../../constants/colors'
import {FUEL_BENZIN} from '../../constants/fuels'
import {IMPLEMENTATION_UNION_V2} from '../../constants/implementations'
import {COMPANY, PRIVATE_PERSON, SELF_EMPLOYED} from '../../constants/legalFormTypes'
import {PAYMENT_FREQUENCY_YEARLY} from '../../constants/paymentFrequencies'
import type {States} from '../../constants/states'
import type {Addons} from '../../constants/unionV2/addons'
import {
  ANIMAL_COVERAGE,
  ASSISTANCE_COVERAGE, ASSISTANCE_COVERAGE_DEFAULT,
  LUGGAGE_COVERAGE, LUGGAGE_COVERAGE_DEFAULT,
  GLASS_COVERAGE,
  INJURY_COVERAGE, INJURY_COVERAGE_DEFAULT,
  ALL_ADDONS, ADDON_ANIMAL, ADDON_LUGGAGE, ADDON_GLASS, ADDON_ELEMENT, ADDON_THEFT, ADDON_LIABILITY,
} from '../../constants/unionV2/addons'
import {ALL_DISCOUNTS, DISCOUNT_UZP} from '../../constants/unionV2/discounts'
import {ALL_PACKAGE_COVERAGES, PACKAGE_COVERAGE_OPTIMUM} from '../../constants/unionV2/packageCoverages'
import {VEHICLE_TYPE_CAR_M1} from '../../constants/vehicleTypes'
import {VEHICLE_USAGE_BASIC} from '../../constants/vehicleUsages'
import {
  isAddonSupported, differenceBetweenRegAndProdIsValid, BEGIN_DATE_MAX_DAYS, newCarOdometerIsValid,
  isNewCar,
} from '../unionV2/utils'
import {keysAsEnum} from '../utils'
import * as common from './common'
import * as refine from './refine'
import * as superRefine from './superRefine'
import * as types from './types'
import * as uuids from './uuids'


export const states = (): z.ZodMiniType<keyof States & string> => {
  if (typeof __isClientOnly === 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const states = require('../../constants/states').default
    const unionStates = keysAsEnum(pickBy(states, IMPLEMENTATION_UNION_V2) as States)
    return types.createEnum(unionStates)
  }

  return types.standardString('SK') as z.ZodMiniType<keyof States & string>
}

export const colors = (): z.ZodMiniType<keyof Colors & string> => {
  if (typeof __isClientOnly === 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const colors = require('../../constants/colors')
    const unionColors = keysAsEnum(pickBy(colors.default, IMPLEMENTATION_UNION_V2) as Colors)
    return types.createEnum(unionColors)
  }

  return types.standardString('biela') as z.ZodMiniType<keyof Colors & string>
}

export const packageCoverages = types.createEnum(ALL_PACKAGE_COVERAGES)

export const addons = types.createEnum(ALL_ADDONS)

export const animalCoverages = types.createEnum(keysAsEnum(ANIMAL_COVERAGE))

export const assistanceCoverages = types.createEnum(keysAsEnum(ASSISTANCE_COVERAGE))

export const luggageCoverages = types.createEnum(keysAsEnum(LUGGAGE_COVERAGE))

export const glassCoverages = types.createEnum(keysAsEnum(GLASS_COVERAGE))

export const injuryCoverages = types.createEnum(keysAsEnum(INJURY_COVERAGE))

export const discounts = types.createEnum(ALL_DISCOUNTS)

const validateAddon = (
  ctx: z.core.$RefinementCtx, coverage: string, addon: Addons,
): void => {
  if (!isAddonSupported(addon)) {
    ctx.addIssue({
      code: 'custom',
      path: [coverage],
      message: `Pripoistenie ${addon} nie je podporované.`,
    })
  }
}

const legalForm = z.discriminatedUnion('legalForm', [
  z.object({
    legalForm: z.literal(PRIVATE_PERSON),
    birthday: common.person.birthday,
    bornNumber: common.person.bornNumber,
    firstName: common.person.firstName,
    lastName: common.person.lastName,
    uzpDiscount: z.optional(discounts),
    uzpApplication: z.optional(types.standardString()),
    nationality: states(),
    companyNumber: types.undefinedOptional(),
    companyName: types.undefinedOptional(),
    representativeBirthday: types.undefinedOptional(),
    representativeFirstName: types.undefinedOptional(),
    representativeLastName: types.undefinedOptional(),
    representativeNationality: types.undefinedOptional(),
  }),
  z.object({
    legalForm: z.literal(SELF_EMPLOYED),
    birthday: types.undefinedOptional(),
    bornNumber: types.undefinedOptional(),
    firstName: types.undefinedOptional(),
    lastName: types.undefinedOptional(),
    uzpDiscount: types.undefinedOptional(),
    uzpApplication: types.undefinedOptional(),
    nationality: types.undefinedOptional(),
    companyNumber: common.person.companyNumber,
    companyName: common.person.companyName,
    representativeBirthday: common.person.birthday,
    representativeFirstName: common.person.firstName,
    representativeLastName: common.person.lastName,
    representativeNationality: states(),
  }),
  z.object({
    legalForm: z.literal(COMPANY),
    birthday: types.undefinedOptional(),
    bornNumber: types.undefinedOptional(),
    firstName: types.undefinedOptional(),
    lastName: types.undefinedOptional(),
    uzpDiscount: types.undefinedOptional(),
    uzpApplication: types.undefinedOptional(),
    nationality: types.undefinedOptional(),
    companyNumber: common.person.companyNumber,
    companyName: common.person.companyName,
    representativeBirthday: common.person.birthday,
    representativeFirstName: common.person.firstName,
    representativeLastName: common.person.lastName,
    representativeNationality: states(),
  }),
]).check(
  z.refine(refine.bornNumberMatchesBirthday, refine.bornNumberRefinement),
  z.superRefine(superRefine.unionUzpDiscount),
)

const vehicleRegistration = z.discriminatedUnion('beforeRegistration', [
  z.object({
    beforeRegistration: z.literal(true),
    licensePlate: types.undefinedOptional(),
    registrationDate: types.undefinedOptional(),
    registrationNumber: types.undefinedOptional(),
  }),
  z.object({
    beforeRegistration: z.literal(false),
    licensePlate: common.licensePlate(),
    registrationDate: common.vehicle.registrationDate,
    registrationNumber: common.vehicle.registrationNumber,
  }),
])

const extendedVehicle = z.intersection(
  z.object({
    vin: common.vin(),
    vehicleType: common.vehicleTypeCar(),
    brand: common.vehicle.brand,
    model: common.vehicle.model,
    weight: common.vehicle.weight,
    volume: common.vehicle.volume,
    power: common.vehicle.power,
    fuel: common.vehicle.fuel,
    seats: common.vehicle.seats,
    productionYear: common.vehicle.productionYear,
    vehicleUsage: common.extendedVehicleUsage(),
    color: colors(),
    doors: types.positiveInteger({max: 10, example: 5}),
    transmission: common.vehicle.transmission,
    bodywork: common.vehicle.bodywork,
    odometer: common.vehicle.odometer,
    carIsNew: types.boolean(),
  }),
  vehicleRegistration,
).check(
  z.superRefine(superRefine.checkRegistrationDate),
  z.superRefine(superRefine.volumeForNonElectroFuel),
  z.superRefine(({productionYear, registrationDate, carIsNew, odometer}, ctx) => {
    if (registrationDate && !differenceBetweenRegAndProdIsValid(registrationDate, productionYear)) {
      ctx.addIssue({
        code: 'custom',
        path: ['registrationDate'],
        message: 'Rok registrácie nesmie byť viac ako 1 rok od roku výroby vozidla.',
      })
    }
    if (!newCarOdometerIsValid(carIsNew, odometer)) {
      ctx.addIssue({
        code: 'custom',
        path: ['odometer'],
        message: 'Počet najazdených kilometrov nového vozidla nesmie byť viac ako 50 km.',
      })
    }
    if (carIsNew && !isNewCar(productionYear)) {
      ctx.addIssue({
        code: 'custom',
        path: ['productionYear'],
        message: `Rok výroby nového vozidla musí byť aktuálny rok alebo nanajvýš predošlý rok,
          ak sa zmluva uzatvára v mesiaci január alebo február.`,
      })
    }
    return z.NEVER
  }),
)

const corespAddress = z.discriminatedUnion('correspAddress', [
  z.object({
    correspAddress: z.literal(true),
    correspState: states(),
    correspZipCode: common.person.zipCode,
    correspCity: common.person.city,
    correspStreet: common.person.street,
    correspStreetNo: common.person.streetNo,
  }),
  z.object({
    correspAddress: z.literal(false),
    correspState: types.undefinedOptional(),
    correspZipCode: types.undefinedOptional(),
    correspCity: types.undefinedOptional(),
    correspStreet: types.undefinedOptional(),
    correspStreetNo: types.undefinedOptional(),
  }),
])

const holderBase = {
  insurerIsHolder: z.literal(false),
  holderState: states(),
  holderZipCode: common.person.zipCode,
  holderCity: common.person.city,
  holderStreet: common.person.street,
  holderStreetNo: common.person.streetNo,
}

const holderPrivatePerson = {
  ...holderBase,
  holderLegalForm: z.literal(PRIVATE_PERSON),
  holderBornNumber: common.person.bornNumber,
  holderFirstName: common.person.firstName,
  holderLastName: common.person.lastName,
  holderNationality: states(),
  holderCompanyNumber: types.undefinedOptional(),
  holderCompanyName: types.undefinedOptional(),
}

const holderSelfEmployed = {
  ...holderBase,
  holderLegalForm: z.literal(SELF_EMPLOYED),
  holderBornNumber: types.undefinedOptional(),
  holderFirstName: types.undefinedOptional(),
  holderLastName: types.undefinedOptional(),
  holderNationality: types.undefinedOptional(),
  holderCompanyNumber: common.person.companyNumber,
  holderCompanyName: common.person.companyName,
}

const holderCompany = {
  ...holderBase,
  holderLegalForm: z.literal(COMPANY),
  holderBornNumber: types.undefinedOptional(),
  holderFirstName: types.undefinedOptional(),
  holderLastName: types.undefinedOptional(),
  holderNationality: types.undefinedOptional(),
  holderCompanyNumber: common.person.companyNumber,
  holderCompanyName: common.person.companyName,
}

const holderCorrespAddressTrue = {
  holderCorrespAddress: z.literal(true),
  holderCorrespState: states(),
  holderCorrespZipCode: common.person.zipCode,
  holderCorrespCity: common.person.city,
  holderCorrespStreet: common.person.street,
  holderCorrespStreetNo: common.person.streetNo,
}

const holderCorrespAddressFalse = {
  holderCorrespAddress: z.literal(false),
  holderCorrespState: types.undefinedOptional(),
  holderCorrespZipCode: types.undefinedOptional(),
  holderCorrespCity: types.undefinedOptional(),
  holderCorrespStreet: types.undefinedOptional(),
  holderCorrespStreetNo: types.undefinedOptional(),
}

const holderLegalForm = z.discriminatedUnion('holderLegalForm', [
  z.discriminatedUnion('holderCorrespAddress', [
    z.object({
      ...holderPrivatePerson,
      ...holderCorrespAddressFalse,
    }),
    z.object({
      ...holderPrivatePerson,
      ...holderCorrespAddressTrue,
    }),
  ]),
  z.discriminatedUnion('holderCorrespAddress', [
    z.object({
      ...holderSelfEmployed,
      ...holderCorrespAddressFalse,
    }),
    z.object({
      ...holderSelfEmployed,
      ...holderCorrespAddressTrue,
    }),
  ]),
  z.discriminatedUnion('holderCorrespAddress', [
    z.object({
      ...holderCompany,
      ...holderCorrespAddressFalse,
    }),
    z.object({
      ...holderCompany,
      ...holderCorrespAddressTrue,
    }),
  ]),
])

const holderLegalFormUndefined = {
  holderLegalForm: types.undefinedOptional(),
  holderBornNumber: types.undefinedOptional(),
  holderFirstName: types.undefinedOptional(),
  holderLastName: types.undefinedOptional(),
  holderNationality: types.undefinedOptional(),
  holderCompanyNumber: types.undefinedOptional(),
  holderCompanyName: types.undefinedOptional(),
}

const holderCorrespAddressUndefined = {
  holderCorrespAddress: types.undefinedOptional(),
  holderCorrespState: types.undefinedOptional(),
  holderCorrespZipCode: types.undefinedOptional(),
  holderCorrespCity: types.undefinedOptional(),
  holderCorrespStreet: types.undefinedOptional(),
  holderCorrespStreetNo: types.undefinedOptional(),
}

const holder = z.discriminatedUnion('insurerIsHolder', [
  z.object({
    ...holderLegalFormUndefined,
    ...holderCorrespAddressUndefined,
    insurerIsHolder: z.literal(true),
    holderState: types.undefinedOptional(),
    holderZipCode: types.undefinedOptional(),
    holderCity: types.undefinedOptional(),
    holderStreet: types.undefinedOptional(),
    holderStreetNo: types.undefinedOptional(),
  }),
  holderLegalForm,
])

const ownerBase = {
  insurerIsOwner: z.literal(false),
  ownerState: states(),
  ownerZipCode: common.person.zipCode,
  ownerCity: common.person.city,
  ownerStreet: common.person.street,
  ownerStreetNo: common.person.streetNo,
}

const ownerPrivatePerson = {
  ...ownerBase,
  ownerLegalForm: z.literal(PRIVATE_PERSON),
  ownerBornNumber: common.person.bornNumber,
  ownerFirstName: common.person.firstName,
  ownerLastName: common.person.lastName,
  ownerNationality: states(),
  ownerCompanyNumber: types.undefinedOptional(),
  ownerCompanyName: types.undefinedOptional(),
}

const ownerSelfEmployed = {
  ...ownerBase,
  ownerLegalForm: z.literal(SELF_EMPLOYED),
  ownerBornNumber: types.undefinedOptional(),
  ownerFirstName: types.undefinedOptional(),
  ownerLastName: types.undefinedOptional(),
  ownerNationality: types.undefinedOptional(),
  ownerCompanyNumber: common.person.companyNumber,
  ownerCompanyName: common.person.companyName,
}

const ownerCompany = {
  ...ownerBase,
  ownerLegalForm: z.literal(COMPANY),
  ownerBornNumber: types.undefinedOptional(),
  ownerFirstName: types.undefinedOptional(),
  ownerLastName: types.undefinedOptional(),
  ownerNationality: types.undefinedOptional(),
  ownerCompanyNumber: common.person.companyNumber,
  ownerCompanyName: common.person.companyName,
}

const ownerCorrespAddressTrue = {
  ownerCorrespAddress: z.literal(true),
  ownerCorrespState: states(),
  ownerCorrespZipCode: common.person.zipCode,
  ownerCorrespCity: common.person.city,
  ownerCorrespStreet: common.person.street,
  ownerCorrespStreetNo: common.person.streetNo,
}

const ownerCorrespAddressFalse = {
  ownerCorrespAddress: z.literal(false),
  ownerCorrespState: types.undefinedOptional(),
  ownerCorrespZipCode: types.undefinedOptional(),
  ownerCorrespCity: types.undefinedOptional(),
  ownerCorrespStreet: types.undefinedOptional(),
  ownerCorrespStreetNo: types.undefinedOptional(),
}

const ownerLegalForm = z.discriminatedUnion('ownerLegalForm', [
  z.discriminatedUnion('ownerCorrespAddress', [
    z.object({
      ...ownerPrivatePerson,
      ...ownerCorrespAddressFalse,
    }),
    z.object({
      ...ownerPrivatePerson,
      ...ownerCorrespAddressTrue,
    }),
  ]),
  z.discriminatedUnion('ownerCorrespAddress', [
    z.object({
      ...ownerSelfEmployed,
      ...ownerCorrespAddressFalse,
    }),
    z.object({
      ...ownerSelfEmployed,
      ...ownerCorrespAddressTrue,
    }),
  ]),
  z.discriminatedUnion('ownerCorrespAddress', [
    z.object({
      ...ownerCompany,
      ...ownerCorrespAddressFalse,
    }),
    z.object({
      ...ownerCompany,
      ...ownerCorrespAddressTrue,
    }),
  ]),
])

const ownerLegalFormUndefined = {
  ownerLegalForm: types.undefinedOptional(),
  ownerBornNumber: types.undefinedOptional(),
  ownerFirstName: types.undefinedOptional(),
  ownerLastName: types.undefinedOptional(),
  ownerNationality: types.undefinedOptional(),
  ownerCompanyNumber: types.undefinedOptional(),
  ownerCompanyName: types.undefinedOptional(),
}

const ownerCorrespAddressUndefined = {
  ownerCorrespAddress: types.undefinedOptional(),
  ownerCorrespState: types.undefinedOptional(),
  ownerCorrespZipCode: types.undefinedOptional(),
  ownerCorrespCity: types.undefinedOptional(),
  ownerCorrespStreet: types.undefinedOptional(),
  ownerCorrespStreetNo: types.undefinedOptional(),
}

const owner = z.discriminatedUnion('insurerIsOwner', [
  z.object({
    ...ownerLegalFormUndefined,
    ...ownerCorrespAddressUndefined,
    insurerIsOwner: z.literal(true),
    ownerState: types.undefinedOptional(),
    ownerZipCode: types.undefinedOptional(),
    ownerCity: types.undefinedOptional(),
    ownerStreet: types.undefinedOptional(),
    ownerStreetNo: types.undefinedOptional(),
  }),
  ownerLegalForm,
])

export const extendedDraft = z.intersection(
  z.object({
    implementation: z.literal(IMPLEMENTATION_UNION_V2),
    paymentFrequency: common.paymentFrequency(),
    beginDate: common.date(),
    personalDataForMarketing: types.boolean(),
    packageCoverage: packageCoverages,
    animalCoverage: z.optional(animalCoverages),
    assistanceCoverage: z.optional(assistanceCoverages),
    elementCoverage: z.optional(types.boolean()),
    glassCoverage: z.optional(glassCoverages),
    injuryCoverage: z.optional(injuryCoverages),
    liabilityCoverage: z.optional(types.boolean()),
    luggageCoverage: z.optional(luggageCoverages),
    theftCoverage: z.optional(types.boolean()),
    state: states(),
    zipCode: common.person.zipCode,
    city: common.person.city,
    street: common.person.street,
    streetNo: common.person.streetNo,
    email: common.email(),
    telephoneCode: common.telephoneCode(),
    telephoneNumber: common.telephoneWithoutPrefix(),
    calculationUuid: uuids.calculationUuid,
  }),
  z.intersection(
    extendedVehicle,
    z.intersection(
      legalForm,
      z.intersection(
        corespAddress,
        z.intersection(
          holder,
          owner,
        ),
      ),
    ),
  ),
).check(
  z.superRefine(({
    animalCoverage, elementCoverage, glassCoverage, liabilityCoverage, luggageCoverage, theftCoverage,
  }, ctx) => {
    if (animalCoverage) validateAddon(ctx, 'animalCoverage', ADDON_ANIMAL)
    if (elementCoverage) validateAddon(ctx, 'elementCoverage', ADDON_ELEMENT)
    if (glassCoverage) validateAddon(ctx, 'glassCoverage', ADDON_GLASS)
    if (liabilityCoverage) validateAddon(ctx, 'liabilityCoverage', ADDON_LIABILITY)
    if (luggageCoverage) validateAddon(ctx, 'luggageCoverage', ADDON_LUGGAGE)
    if (theftCoverage) validateAddon(ctx, 'theftCoverage', ADDON_THEFT)
    return z.NEVER
  })
)
export type ExtendedDraft = z.infer<typeof extendedDraft>

export const draft = z.intersection(
  extendedDraft,
  z.object({
    vehicleUsage: common.vehicle.usage,
  }),
)
export type Draft = z.infer<typeof draft>

export const draftNew = z.intersection(
  draft,
  z.object({
    beginDate: common.beginDate({maxDays: BEGIN_DATE_MAX_DAYS}),
  }),
)
export type DraftNew = z.infer<typeof draftNew>

const draftExample = {
  implementation: IMPLEMENTATION_UNION_V2,
  paymentFrequency: PAYMENT_FREQUENCY_YEARLY,
  beginDate: format(new Date(), 'yyyy-MM-dd'),
  personalDataForMarketing: true,
  packageCoverage: PACKAGE_COVERAGE_OPTIMUM,
  assistanceCoverage: ASSISTANCE_COVERAGE_DEFAULT,
  injuryCoverage: INJURY_COVERAGE_DEFAULT,
  liabilityCoverage: true,
  luggageCoverage: LUGGAGE_COVERAGE_DEFAULT,
  licensePlate: 'BA111EX',
  vin: 'AAAAAAAAAA0000000',
  vehicleType: VEHICLE_TYPE_CAR_M1,
  brand: 'bmw',
  model: 'rad5',
  weight: 2320,
  volume: 2926,
  power: 159,
  fuel: FUEL_BENZIN,
  seats: 5,
  productionYear: 2021,
  beforeRegistration: false,
  registrationDate: '2021-02-20',
  registrationNumber: 'AA123456',
  vehicleUsage: VEHICLE_USAGE_BASIC,
  color: 'biela',
  doors: 5,
  transmission: 'm5',
  bodywork: 'sedan',
  odometer: 50000,
  carIsNew: false,
  legalForm: PRIVATE_PERSON,
  state: 'SK',
  zipCode: '91708',
  city: 'Trnava',
  street: 'Letná',
  streetNo: '3',
  correspAddress: false,
  email: 'foo@example.com',
  telephoneCode: '+421',
  telephoneNumber: '903123456',
  birthday: '1980-03-24',
  bornNumber: '8003241466',
  firstName: 'Ferko',
  lastName: 'Mrkvicka',
  nationality: 'SK',
  insurerIsHolder: true,
  insurerIsOwner: true,
  uzpDiscount: DISCOUNT_UZP,
  calculationUuid: 'XXXXX',
}

export const extendedDraftNew = z.intersection(
  extendedDraft,
  z.object({
    beginDate: common.beginDate({maxDays: BEGIN_DATE_MAX_DAYS}),
  }),
).register(z.globalRegistry, {example: draftExample})
export type ExtendedDraftNew = z.infer<typeof extendedDraftNew>

export const accept = z.object({
  uuid: uuids.unionContractUuid,
})
export type Accept = z.infer<typeof accept>

export const document = z.object({
  uuid: uuids.unionDocumentUuid,
})
export type Document = z.infer<typeof document>

export const record = z.object({
  uuid: uuids.unionContractUuid,
})
export type Record = z.infer<typeof record>

export const templateContractEmailVariables = z.object({
  templateId: types.standardString(),
  contractNumber: types.standardString(),
  licensePlate: z.optional(common.licensePlate()),
  brandLabel: types.standardString(),
  modelLabel: types.standardString(),
  firstName: common.person.firstName,
  lastName: common.person.lastName,
})

export type TemplateContractEmailVariables = z.infer<typeof templateContractEmailVariables>
